// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import cartSummarySharedStyles, { GridItemWithText } from "@keller/App/SharedStyle/Cart/CartSummarySharedStyles";
import { pluralize } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderTotalDisplayStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPricing: state.context.showPricing,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface OrderTotalDisplayStyles {
    container?: GridContainerProps;
    subtotalLabelGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValueGridItem?: GridItemProps;
    subtotalValue?: TypographyProps;
    discountsLabelGridItem?: GridItemProps;
    discountsLabel?: TypographyProps;
    discountsValueGridItem?: GridItemProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabelGridItem?: GridItemProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValueGridItem?: GridItemProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabelGridItem?: GridItemProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValueGridItem?: GridItemProps;
    otherChargesValue?: TypographyProps;
    taxLabelGridItem?: GridItemProps;
    taxLabel?: TypographyProps;
    taxValueGridItem?: GridItemProps;
    taxValue?: TypographyProps;
    totalLabelGridItem?: GridItemProps;
    totalLabel?: TypographyProps;
    totalValueGridItem?: GridItemProps;
    totalValue?: TypographyProps;
    // XNG-Changes
    titleItem?: GridItemWithText;
    itemsCountItem?: GridItemWithText;
    topSeparator?: GridItemProps;
    bottomSeparator?: GridItemProps;
}

export const orderTotalDisplayStyles: OrderTotalDisplayStyles = {
    container: {
        ...cartSummarySharedStyles.container,
    },
    subtotalLabelGridItem: {
        ...cartSummarySharedStyles.labelGridItem,
    },
    subtotalValueGridItem: {
        ...cartSummarySharedStyles.valueGridItem,
    },
    subtotalLabel: {
        ...cartSummarySharedStyles.label,
    },
    subtotalValue: {
        ...cartSummarySharedStyles.value,
    },
    discountsLabelGridItem: {
        ...cartSummarySharedStyles.labelGridItem,
    },
    discountsValueGridItem: {
        ...cartSummarySharedStyles.valueGridItem,
    },
    discountsLabel: {
        ...cartSummarySharedStyles.label,
    },
    discountsValue: {
        ...cartSummarySharedStyles.value,
    },
    shippingAndHandlingLabelGridItem: {
        ...cartSummarySharedStyles.labelGridItem,
    },
    shippingAndHandlingValueGridItem: {
        ...cartSummarySharedStyles.valueGridItem,
    },
    shippingAndHandlingLabel: {
        ...cartSummarySharedStyles.label,
    },
    shippingAndHandlingValue: {
        ...cartSummarySharedStyles.value,
    },
    otherChargesLabelGridItem: {
        ...cartSummarySharedStyles.labelGridItem,
    },
    otherChargesValueGridItem: {
        ...cartSummarySharedStyles.valueGridItem,
    },
    otherChargesLabel: {
        ...cartSummarySharedStyles.label,
    },
    otherChargesValue: {
        ...cartSummarySharedStyles.value,
    },
    //
    taxLabelGridItem: {
        ...cartSummarySharedStyles.labelGridItem,
    },
    taxValueGridItem: {
        ...cartSummarySharedStyles.valueGridItem,
    },
    taxLabel: {
        ...cartSummarySharedStyles.label,
    },
    taxValue: {
        ...cartSummarySharedStyles.value,
    },
    totalLabelGridItem: {
        ...cartSummarySharedStyles.totalLabelGridItem,
    },
    totalLabel: {
        ...cartSummarySharedStyles.totalLabel,
    },
    totalValueGridItem: {
        ...cartSummarySharedStyles.totalValueGridItem,
    },
    totalValue: {
        ...cartSummarySharedStyles.totalValue,
    },
    titleItem: {
        ...cartSummarySharedStyles.titleItem,
    },
    itemsCountItem: {
        ...cartSummarySharedStyles.itemsCountItem,
    },
    topSeparator: {
        ...cartSummarySharedStyles.topSeparator,
    },
    bottomSeparator: {
        ...cartSummarySharedStyles.bottomSeparator,
    },
};

const OrderTotalDisplay: FC<Props> = ({ order, enableVat, showPricing, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(orderTotalDisplayStyles, extendedStyles));

    if (!order || !showPricing) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.titleItem?.container}>
                <Typography {...styles.titleItem?.text}>{translate("Order Summary")}</Typography>
            </GridItem>
            <GridItem {...styles.itemsCountItem?.container}>
                <Typography {...styles.itemsCountItem?.text}>{`${order.orderLines?.length || 0} ${translate(
                    pluralize(order.orderLines?.length || 0, "Item"),
                )}`}</Typography>
            </GridItem>
            {/* Xng-change */}
            <GridItem {...styles.topSeparator}>
                <StyledWrapper />
            </GridItem>
            <GridItem {...styles.subtotalLabelGridItem}>
                <Typography {...styles.subtotalLabel}>
                    {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                </Typography>
            </GridItem>
            <GridItem {...styles.subtotalValueGridItem}>
                <Typography {...styles.subtotalValue}>{order.orderSubTotalDisplay}</Typography>
            </GridItem>
            {order.discountAmount > 0 && (
                <>
                    <GridItem {...styles.discountsLabelGridItem}>
                        <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                    </GridItem>
                    <GridItem {...styles.discountsValueGridItem}>
                        <Typography
                            {...styles.discountsValue}
                            data-test-selector="discountAmount"
                        >{`-${order.orderDiscountAmountDisplay}`}</Typography>
                    </GridItem>
                </>
            )}

            {/* XNG-Change */}
            {order.showTaxAndShipping && (
                <>
                    <GridItem {...styles.shippingAndHandlingLabelGridItem}>
                        <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping & Handling")}</Typography>
                    </GridItem>
                    <GridItem {...styles.shippingAndHandlingValueGridItem}>
                        <Typography {...styles.shippingAndHandlingValue}>{translate("TBD")}</Typography>
                    </GridItem>
                </>
            )}

            {order.otherCharges > 0 && (
                <>
                    <GridItem {...styles.otherChargesLabelGridItem}>
                        <Typography {...styles.otherChargesLabel}>{translate("Other Charges")}</Typography>
                    </GridItem>
                    <GridItem {...styles.otherChargesValueGridItem}>
                        <Typography {...styles.otherChargesValue}>{order.otherChargesDisplay}</Typography>
                    </GridItem>
                </>
            )}
            {(!order.orderHistoryTaxes || order.orderHistoryTaxes.length === 0) && (
                <>
                    <GridItem {...styles.taxLabelGridItem}>
                        <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                    </GridItem>
                    <GridItem {...styles.taxValueGridItem}>
                        <Typography {...styles.taxValue} data-test-selector="orderDetails_totalTaxDisplay">
                            {translate("TBD")}
                        </Typography>
                    </GridItem>
                </>
            )}
            {order.orderHistoryTaxes &&
                order.orderHistoryTaxes.map(tax => (
                    <>
                        <GridItem {...styles.taxLabelGridItem}>
                            <Typography {...styles.taxLabel}>{tax.taxDescription || translate("Tax")}</Typography>
                        </GridItem>
                        <GridItem {...styles.taxValueGridItem}>
                            <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                        </GridItem>
                    </>
                ))}
            {/* Xng-change */}
            <GridItem {...styles.bottomSeparator}>
                <StyledWrapper />
            </GridItem>

            <GridItem {...styles.totalLabelGridItem}>
                <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
            </GridItem>
            <GridItem {...styles.totalValueGridItem}>
                <Typography {...styles.totalValue} data-test-selector="orderDetail_orderGrandTotalDisplay">
                    {/* XNG-Change */}
                    {translate("TBD")}
                </Typography>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(OrderTotalDisplay);
